import type {
    CreateDmDoctorBody,
    CreateDmPracticeBody,
    DmAddress
} from '../../../../../services/practiceDm';
import { isE164 } from './dmPhone.ts';

export const DM_LOCALES = ['en', 'en_GB', 'en_AU', 'fr', 'es', 'de', 'it', 'nl', 'pt', 'pt_BR'];
export const DEFAULT_DM_LOCALE = 'en';
const REGION_REQUIRED_COUNTRIES = ['US', 'CA'];
const TWO_LETTER_CODE = /^[A-Z]{2}$/;
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export interface DmAddressForm {
    name: string;
    email: string;
    address: string;
    zipCode: string;
    city: string;
    regionCode: string;
}

export interface DmPracticeForm {
    locale: string;
    phone: string;
    firstname: string;
    lastname: string;
    countryCode: string;
    shipping: DmAddressForm;
    billingSameAsShipping: boolean;
    billing: DmAddressForm;
}

export interface DmDoctorForm {
    firstname: string;
    lastname: string;
    email: string;
    phone: string;
    locale: string;
}

/** Keyed by field path, e.g. `shipping.city`. */
export type DmFormErrors = Record<string, string>;

export type DmPracticeDraft = Omit<CreateDmPracticeBody, 'idempotencyKey'>;
export type DmDoctorDraft = Omit<CreateDmDoctorBody, 'idempotencyKey'>;

type FieldCheck = [path: string, error: string | null];

function requiredError(value: string): string | null {
    return value.trim() === '' ? 'Required' : null;
}

function emailError(value: string): string | null {
    const trimmed = value.trim();
    if (trimmed === '') return 'Required';
    return EMAIL_PATTERN.test(trimmed) ? null : 'Enter a valid email address';
}

function phoneError(value: string): string | null {
    const trimmed = value.trim();
    if (trimmed === '') return 'Required';
    return isE164(trimmed) ? null : 'Use E164 format, e.g. +15551234567';
}

export function requiresRegionCode(countryCode: string): boolean {
    return REGION_REQUIRED_COUNTRIES.includes(countryCode.trim().toUpperCase());
}

function regionError(regionCode: string, countryCode: string): string | null {
    const region = regionCode.trim().toUpperCase();
    if (region === '') return requiresRegionCode(countryCode) ? 'Required' : null;
    return TWO_LETTER_CODE.test(region) ? null : 'Use the 2-letter state or province code';
}

function addressChecks(prefix: string, address: DmAddressForm, countryCode: string): FieldCheck[] {
    return [
        [`${prefix}.name`, requiredError(address.name)],
        [`${prefix}.email`, emailError(address.email)],
        [`${prefix}.address`, requiredError(address.address)],
        [`${prefix}.zipCode`, requiredError(address.zipCode)],
        [`${prefix}.city`, requiredError(address.city)],
        [`${prefix}.regionCode`, regionError(address.regionCode, countryCode)]
    ];
}

function collectErrors(checks: FieldCheck[]): DmFormErrors {
    return Object.fromEntries(
        checks.filter((check): check is [string, string] => check[1] !== null)
    );
}

export function validateDmPracticeForm(form: DmPracticeForm): DmFormErrors {
    const countryCode = form.countryCode.trim().toUpperCase();
    return collectErrors([
        ['firstname', requiredError(form.firstname)],
        ['lastname', requiredError(form.lastname)],
        ['phone', phoneError(form.phone)],
        ['countryCode', TWO_LETTER_CODE.test(countryCode) ? null : 'Use a 2-letter code'],
        ...addressChecks('shipping', form.shipping, countryCode),
        ...(form.billingSameAsShipping ? [] : addressChecks('billing', form.billing, countryCode))
    ]);
}

export function validateDmDoctorForm(form: DmDoctorForm): DmFormErrors {
    return collectErrors([
        ['firstname', requiredError(form.firstname)],
        ['lastname', requiredError(form.lastname)],
        ['email', emailError(form.email)],
        ['phone', phoneError(form.phone)]
    ]);
}

// DM's region format is the country and state joined by a double underscore, e.g. US__TX.
function toDmRegionCode(regionCode: string, countryCode: string): string | null {
    const region = regionCode.trim().toUpperCase();
    return region === '' ? null : `${countryCode}__${region}`;
}

function toDmAddress(address: DmAddressForm, countryCode: string): DmAddress {
    return {
        name: address.name.trim(),
        email: address.email.trim(),
        address: address.address.trim(),
        zipCode: address.zipCode.trim(),
        city: address.city.trim(),
        regionCode: toDmRegionCode(address.regionCode, countryCode)
    };
}

export function buildDmPracticeDraft(form: DmPracticeForm): DmPracticeDraft {
    const countryCode = form.countryCode.trim().toUpperCase();
    return {
        locale: form.locale,
        phone: form.phone.trim(),
        firstname: form.firstname.trim(),
        lastname: form.lastname.trim(),
        countryCode,
        shipping: toDmAddress(form.shipping, countryCode),
        billing: form.billingSameAsShipping ? null : toDmAddress(form.billing, countryCode)
    };
}

// The first practice id is the main one.
export function buildDmDoctorDraft(form: DmDoctorForm, practiceIds: number[]): DmDoctorDraft {
    return {
        firstname: form.firstname.trim(),
        lastname: form.lastname.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        locale: form.locale,
        practiceIds: [...new Set(practiceIds)],
        mainPracticeId: practiceIds[0]
    };
}
